// Front-end screen flow as pure transitions (no DOM), so main.ts only renders what these return.
//   splash → menu → { online → [name] → lobby, bots → heroSelect → match, replays → replay, editor [→ heroSelect → match], heroes, settings }
// Every screen but splash, match and replay goes back one step (Esc / «Назад»); menu goes back to splash.
// ?replay=<id> is a shared link: the splash first (the gesture that unlocks sound), then straight to the viewer.

export type Screen = 'splash' | 'menu' | 'settings' | 'heroes' | 'heroSelect' | 'name' | 'lobby' | 'match' | 'editor' | 'replays' | 'replay';
export type MenuItem = 'online' | 'bots' | 'replays' | 'editor' | 'heroes' | 'settings';

/** Hash a finished match (or the lobby's «В меню») reloads to: straight to the menu, no splash. */
export const MENU_HASH = '#menu';
/** Hash that opens the map editor directly (the dev server's /editor redirects here). */
export const EDITOR_HASH = '#editor';
/** Hash the replay viewer's «Выход» reloads to when it was opened from the list: back to «Мои повторы». */
export const REPLAYS_HASH = '#replays';
/** With ?replay=<id>: opened from the results screen, the file may still be being written (retry a 404). */
export const FRESH_REPLAY_HASH = '#fresh';

export type Arrival = {
  params: URLSearchParams; hash: string; hasName: boolean;
  isHero(id: string): boolean; isRoom(id: string): boolean; isReplay?(id: string): boolean;
};

/** The ?replay=<id> of this arrival, when it is a well-formed id. */
export function replayParam(params: URLSearchParams, isReplay: (id: string) => boolean): string | null {
  const id = params.get('replay');
  return id !== null && isReplay(id) ? id : null;
}

/** ?hero=<id> is a direct bot match, ?online a direct lobby link, ?room=<id> a direct link into one
    room (a friend's invite) — none of the three see the splash or menu. A malformed/unrecognised ?room
    is silently ignored (falls through to the ordinary splash/menu path) rather than treated as ?online:
    a mistyped or truncated link should not drop the player into the anonymous room list. */
export function initialScreen({ params, hash, hasName, isHero, isRoom, isReplay = () => false }: Arrival): Screen {
  const hero = params.get('hero');
  if (hero && isHero(hero)) return 'match';
  // Arriving from inside the game (a reload to one of its own hashes) needs no splash; a link from outside does.
  if (replayParam(params, isReplay)) return [MENU_HASH, REPLAYS_HASH, FRESH_REPLAY_HASH].includes(hash) ? 'replay' : 'splash';
  const room = params.get('room');
  if ((room && isRoom(room)) || params.has('online')) return hasName ? 'lobby' : 'name';
  return hash === MENU_HASH ? 'menu' : hash === EDITOR_HASH ? 'editor' : hash === REPLAYS_HASH ? 'replays' : 'splash';
}

/** Where the splash leads: a shared replay link goes straight to its viewer. */
export function afterSplash(hasReplay: boolean): Screen {
  return hasReplay ? 'replay' : 'menu';
}

export function chooseMenuItem(item: MenuItem, hasName: boolean): Screen {
  switch (item) {
    case 'online': return hasName ? 'lobby' : 'name';
    case 'bots': return 'heroSelect';
    case 'editor': return 'editor';
    case 'replays': return 'replays';
    case 'heroes': return 'heroes';
    case 'settings': return 'settings';
  }
}

/** null: no back from here (splash waits for any key; a match ends through its own banner). */
export function backFrom(screen: Screen): Screen | null {
  switch (screen) {
    case 'splash': case 'match': case 'replay': return null;
    case 'menu': return 'splash';
    default: return 'menu';
  }
}
